import { NEW_UI } from '../../app/features';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import type { Session } from '../../api/session';
import { deleteAccount, getNewMessagesCount, getObject, getProfile, type ObjectInfo } from '../../api/tenant';
import { SUPPORT_URL } from '../../app/config';
import { appInviteMessage } from '../../domain/deepLink';
import { formatPhone, initials, telHref } from '../../domain/people';
import { telegramPhotoUrl } from '../../domain/telegramPhoto';
import type { TelegramApi } from '../../telegram/webapp';
import { Avatar } from '../../ui/Avatar';
import { MessageScreen } from '../auth/screens';
import { useHomeScreen } from '../shortcut/useHomeScreen';
import c from '../pass-card/passCard.module.css';
import p from '../passes/passes.module.css';
import s from './more.module.css';


function MenuLink({ to, label, value, badge }: { to: string; label: string; value?: string; badge?: number }) {
  return (
    <li>
      <Link to={to} className={s.item}>
        <span className={s.itemLabel}>{label}</span>
        {value && <span className={s.itemValue}>{value}</span>}
        {!!badge && <span className={s.badge}>{badge}</span>}
      </Link>
    </li>
  );
}

const HIDDEN = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' } as const;

/** «Ещё» (spec §6.9). */
export function MoreScreen({ session, telegram, object }: { session: Session; telegram: TelegramApi; object: ObjectInfo }) {
  const profile = useQuery({ queryKey: ['profile'], queryFn: () => getProfile(session) });
  const unread = useQuery({ queryKey: ['new-count'], queryFn: () => getNewMessagesCount(session) });
  const details = useQuery({ queryKey: ['object', object.id], queryFn: () => getObject(session, object.id) });
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const remove = useMutation({ mutationFn: () => deleteAccount(session) });
  const shortcut = useHomeScreen(telegram);

  if (remove.isSuccess) {
    return <MessageScreen title="Аккаунт удалён" text="Вы отвязаны от всех адресов. Чтобы снова пользоваться пропусками, обратитесь в УК. Приложение можно закрыть." />;
  }

  const pr = profile.data;
  const st = details.data?.settings ?? {};
  const confidences = st.confidencesEnable === true || st.enableRequestsForConfidences === true;
  const phone = details.data?.phone ?? object.phone;

  return (
    <div className={p.page}>
      {/* The tab bar already says «Ещё»: the new interface keeps the heading for screen readers only (#4216). */}
      <h1 className={p.title} style={NEW_UI ? HIDDEN : undefined}>Ещё</h1>
      <Link to="/more/profile" className={s.profile}>
        <Avatar className={s.avatar} photoUrl={telegramPhotoUrl(telegram.initData)} initials={initials(pr?.firstName, pr?.lastName)} />
        <span className={s.who}>
          <span className={s.whoName}>{pr ? [pr.firstName, pr.lastName].filter(Boolean).join(' ') : 'Профиль'}</span>
          <span className={s.whoSub}>{pr ? `${formatPhone(pr.phone)} · из ${telegram.name}` : ' '}</span>
        </span>
      </Link>

      <button
        type="button"
        className={s.inviteApp}
        onClick={() => telegram.share(telegram.appLink(`obj_${object.id}`), appInviteMessage(object.name, telegram.name))}
      >
        <span className={s.inviteAppTitle}>Пригласить семью и соседей</span>
        <span className={s.inviteAppSub}>Пусть тоже заказывают пропуска сами — ссылка откроет ваш посёлок</span>
      </button>

      {NEW_UI && (
        // The new home screen keeps only the main order; the special passes live here.
        <ul className={s.menu}>
          <MenuLink to="/crew" label="Несколько гостей" />
          <MenuLink to="/regular" label="Регулярный гость" />
        </ul>
      )}
      <ul className={s.menu}>
        <MenuLink to="/more/guests" label={NEW_UI ? 'Шаблоны' : 'Частые гости'} />
        <MenuLink to="/more/cars" label="Мои машины" />
        {confidences && <MenuLink to="/more/confidances" label="Доверенности" />}
        <MenuLink to="/more/addresses" label="Мои адреса" />
      </ul>
      <ul className={s.menu}>
        <MenuLink to="/notifications" label="Уведомления" badge={unread.data} />
        <MenuLink to="/more/feed" label="Лента событий" />
        <MenuLink to="/more/join" label="Присоединиться к объекту" />
      </ul>
      <ul className={s.menu}>
        {phone && (
          <li>
            <a href={telHref(phone)} className={s.item}>
              <span className={s.itemLabel}>Позвонить в УК</span>
              <span className={s.itemValue}>{phone}</span>
            </a>
          </li>
        )}
        <li>
          <button type="button" className={s.item} onClick={() => telegram.openLink(SUPPORT_URL)}>
            <span className={s.itemLabel}>Техподдержка PASS24</span>
            <span className={s.itemValue}>support.pass24pro.ru</span>
          </button>
        </li>
        {shortcut.canAdd && (
          <li>
            <button type="button" className={s.item} onClick={shortcut.add}>
              <span className={s.itemLabel}>Ярлык на рабочий стол</span>
            </button>
          </li>
        )}
        <MenuLink to="/?intro=1" label="Как пользоваться" />
        <MenuLink to="/more/whats-new" label="Что нового" value="версия 1.0" />
      </ul>

      {step === 0 && (
        <button type="button" className={s.delete} onClick={() => setStep(1)}>
          Удалить аккаунт
        </button>
      )}
      {step > 0 && (
        <div className={c.confirm} role="group" aria-label="Удаление аккаунта">
          {step === 1 ? (
            <>
              <p className={c.note}>Удаление отвяжет вас от всех адресов и удалит учётную запись. Вернуть доступ сможет только УК.</p>
              <button type="button" className={c.ghost} onClick={() => setStep(2)}>
                Продолжить
              </button>
            </>
          ) : (
            <>
              <p className={c.note}>Точно удалить? Пропуска на ваши адреса больше нельзя будет заказать.</p>
              <button type="button" className={c.danger} disabled={remove.isPending} onClick={() => remove.mutate()}>
                Удалить навсегда
              </button>
            </>
          )}
          <button type="button" className={c.ghost} onClick={() => setStep(0)}>
            Отмена
          </button>
          {remove.isError && <p className={c.note}>Не удалось удалить аккаунт. Попробуйте ещё раз или напишите в поддержку.</p>}
        </div>
      )}
    </div>
  );
}
